import { getScoreSections, platformNameConverter } from '../common-utilities';
import type {
  IDynamicContent,
  IEventHandlers,
  IAppDetailsAppData,
} from '../common-types';
import useSWR from 'swr';
import { ScoreDetailsView } from './ScoreDetailsView';

type ScoreDetailsProps = {
  platformName: string;
  app: IAppDetailsAppData;
  locale: string;
  dynamicContentApiUrl: string;
  eventHandlers: IEventHandlers;
  translatedText: {
    platformScoreDetailsHeading: string;
    platformScoreDetailsMissingDynamicContent: string;
  };
};

/**
 * ScoreDetails from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param locale is required, default to "en-GB" if not provided
 * @param translatedText is required for the ScoreDetailsView component, this is the text that as already been translated by the consumer of this component.
 */

export const ScoreDetails = ({
  platformName,
  app,
  eventHandlers,
  locale = 'en-GB',
  dynamicContentApiUrl,
  translatedText,
  ...props
}: ScoreDetailsProps) => {
  const { platforms } = app ?? {};
  const relevantPlatform = platforms?.find(
    (p) => platformName === platformNameConverter(p.platformName)
  );

  const reviewId = relevantPlatform?.reviewId;

  const localeQuery = locale ? `&language=${locale}` : '';

  const fetcher = (url: string) => fetch(url).then((res) => res.json());

  const { data: dynamicContent, isLoading } = useSWR<IDynamicContent>(
    dynamicContentApiUrl && reviewId && localeQuery
      ? `${dynamicContentApiUrl}?reviewId=${reviewId}${localeQuery}`
      : null,
    fetcher
  );

  const basicSections =
    getScoreSections(relevantPlatform?.scoreSections ?? [], dynamicContent) ??
    [];

  return (
    <ScoreDetailsView
      translatedText={translatedText}
      {...props}
      eventHandlers={eventHandlers}
      dynamicContent={basicSections}
      isLoading={isLoading}
    />
  );
};
